import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';

/**
 * ThemeContext — warm-light / warm-dark theme with system-default option.
 *
 * Behaviour
 *   • `theme` is the user's stored *preference*: 'light' | 'dark' | 'system'
 *   • `resolvedTheme` is the actually-applied theme: 'light' | 'dark'
 *     (resolves 'system' against `prefers-color-scheme`).
 *   • Stored in `localStorage['norvig.theme']` so it survives reloads.
 *   • System-preference listener: when user picks 'system', a media-query
 *     watcher flips `resolvedTheme` live when the OS theme changes.
 *
 * DOM contract
 *   On every change we add/remove the `dark` class on `<html>` (Tailwind's
 *   `darkMode: 'class'` convention — already configured in tailwind.config.js).
 *   That single class drives all `dark:`-prefixed utilities and the broader
 *   warm-dark overrides we keep in `index.css`.
 */
const STORAGE_KEY = 'norvig.theme';

const ThemeContext = createContext({
  theme: 'system',
  resolvedTheme: 'light',
  setTheme: () => {},
  cycleTheme: () => {},
});

const readStoredTheme = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    // ignore localStorage errors (private mode, etc.)
  }
  return 'system';
};

const systemPrefersDark = () => {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
};

const resolve = (theme) => (theme === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : theme);

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => readStoredTheme());
  const [resolvedTheme, setResolvedTheme] = useState(() => resolve(readStoredTheme()));

  // Apply class to <html> + persist
  useEffect(() => {
    const next = resolve(theme);
    setResolvedTheme(next);
    const root = document.documentElement;
    if (next === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
    try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* ignore */ }
  }, [theme]);

  // Live-watch system preference when user picks 'system'
  useEffect(() => {
    if (theme !== 'system') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      const next = mq.matches ? 'dark' : 'light';
      setResolvedTheme(next);
      const root = document.documentElement;
      if (next === 'dark') root.classList.add('dark');
      else root.classList.remove('dark');
    };
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [theme]);

  const setTheme = useCallback((v) => {
    if (v === 'light' || v === 'dark' || v === 'system') setThemeState(v);
  }, []);

  // light → dark → system → light
  const cycleTheme = useCallback(() => {
    setThemeState((t) => (t === 'light' ? 'dark' : t === 'dark' ? 'system' : 'light'));
  }, []);

  const value = useMemo(
    () => ({ theme, resolvedTheme, setTheme, cycleTheme }),
    [theme, resolvedTheme, setTheme, cycleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
